import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import type { ConsumptionBySector } from '../../../api/wattelzce-demo';
import { useI18n } from '../../../i18n';

interface Co2BySectorChartProps {
  data: ConsumptionBySector[];
  loading?: boolean;
}

function Co2BySectorChart({ data, loading }: Co2BySectorChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t, locale } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Sort by CO2 and take all
  const sortedData = [...data].sort((a, b) => b.co2 - a.co2);

  // Color gradient from red (high) to green (low)
  const getColor = (index: number, total: number) => {
    if (total <= 1) return '#dc2626';
    const ratio = index / (total - 1);
    // From red to yellow to green
    if (ratio < 0.3) return '#dc2626';
    if (ratio < 0.5) return '#f97316';
    if (ratio < 0.7) return '#f59e0b';
    if (ratio < 0.85) return '#84cc16';
    return '#22c55e';
  };

  const chartData = sortedData.map((d, index) => ({
    name: d.zoneLabel || d.sector,
    fullName: d.sector,
    y: d.co2,
    energy: d.energy,
    cost: d.cost,
    color: getColor(index, sortedData.length),
  }));

  const categories = sortedData.map((d, index) => d.zoneLabel || t('leg2_zone_n', { n: index + 1 }));

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: 320,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: t('co2BySectorTitle'),
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: t('co2BySectorSub'),
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '11px',
          color: '#374151',
        },
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      min: 0,
      title: {
        text: `${t('co2')} (${t('kg')})`,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value:,.0f}',
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      useHTML: true,
      formatter: function() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx = this as any;
        const point = ctx.point?.options || {};
        return `
          <div style="padding: 8px;">
            <strong>${point.fullName || ctx.x}</strong><br/>
            <span style="color: #dc2626;">🌍 ${t('co2')}:</span> <b>${point.y?.toLocaleString(locale, {maximumFractionDigits: 1})} ${t('kg')}</b><br/>
            <span style="color: #059669;">⚡ ${t('energy')}:</span> ${point.energy?.toLocaleString(locale, {maximumFractionDigits: 1})} ${t('kwh')}<br/>
            <span style="color: #2563eb;">💰 ${t('cost')}:</span> ${point.cost?.toLocaleString(locale, {maximumFractionDigits: 2})} ${t('euro')}
          </div>
        `;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: `{point.y:,.0f} ${t('kg')}`,
          style: {
            fontSize: '10px',
            fontWeight: '600',
            color: '#374151',
          },
        },
      },
    },
    series: [{
      type: 'bar',
      name: t('co2'),
      data: chartData,
      colorByPoint: true,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

export default Co2BySectorChart;
